import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Divider,
  Paper
} from '@mui/material';
import { API_BASE_URL } from '../utils/api';

interface Category {
  id: number;
  name: string;
}

const ProviderRegister: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState({
    businessName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    description: '',
    services: [] as string[],
    serviceArea: '',
    operatingHours: '',
    website: '',
    facebook: '',
    instagram: '',
    photos: [] as string[],
    selectedCategories: [] as number[]
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (err: any) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleServicesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const servicesArray = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
    setForm({ ...form, services: servicesArray });
  };

  const handleCategoryChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setForm({ ...form, selectedCategories: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    // Validate required fields
    const requiredFields = ['businessName', 'contactPerson', 'phone', 'address', 'description', 'serviceArea', 'operatingHours'];
    for (const field of requiredFields) {
      if (!form[field as keyof typeof form]) {
        setMessage({ type: 'error', text: `${field.charAt(0).toUpperCase() + field.slice(1)} is required.` });
        setLoading(false);
        return;
      }
    }

    if (form.services.length === 0) {
      setMessage({ type: 'error', text: 'At least one service is required.' });
      setLoading(false);
      return;
    }

    if (form.selectedCategories.length === 0) {
      setMessage({ type: 'error', text: 'At least one category is required.' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: form.businessName,
          contactPerson: form.contactPerson,
          phone: form.phone,
          email: form.email || undefined,
          address: form.address,
          description: form.description,
          services: form.services,
          serviceArea: form.serviceArea,
          operatingHours: form.operatingHours,
          website: form.website || undefined,
          facebook: form.facebook || undefined,
          instagram: form.instagram || undefined,
          photos: form.photos,
          categories: form.selectedCategories,
          status: 'pending',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to register provider');
      }

      setMessage({ type: 'success', text: data.message || 'Registration submitted! Awaiting admin approval.' });
      
      // Reset form
      setForm({
        businessName: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: '',
        description: '',
        services: [],
        serviceArea: '',
        operatingHours: '',
        website: '',
        facebook: '',
        instagram: '',
        photos: [],
        selectedCategories: []
      });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to register provider. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Service Provider Registration
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 4 }}>
          Register your business to provide services to our community
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
              Basic Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Business Name"
                name="businessName"
                value={form.businessName}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="Enter your business name"
              />
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                name="contactPerson"
                value={form.contactPerson}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="Full name of contact person"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                  label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="+27 12 345 6789"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                  label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                  placeholder="business@example.com"
              />
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                  label="Business Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="123 Main Street, City, Province"
              />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Business Details Section */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
              Business Details
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                  label="Business Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                  rows={4}
                required
                  placeholder="Describe your business, services, and what makes you unique..."
              />
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                label="Service Area"
                name="serviceArea"
                value={form.serviceArea}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="e.g., Cape Town CBD, Northern Suburbs"
              />
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                label="Operating Hours"
                name="operatingHours"
                value={form.operatingHours}
                onChange={handleChange}
                fullWidth
                required
                  placeholder="e.g., Mon-Fri 8:00 AM - 6:00 PM"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                  label="Services Offered (comma-separated)"
                  name="services"
                  value={form.services.join(', ')}
                  onChange={handleServicesChange}
                  fullWidth
                  required
                  placeholder="e.g., Plumbing, Drain Cleaning, Pipe Repair, Installation"
                  helperText="Enter services separated by commas"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Service Categories</InputLabel>
                  <Select
                    multiple
                    value={form.selectedCategories}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Service Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const category = categories.find(cat => cat.id === value);
                          return (
                            <Chip key={value} label={category?.name || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categoriesLoading ? (
                      <MenuItem disabled>Loading categories...</MenuItem>
                    ) : (
                      categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Additional Information Section */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
              Additional Information (Optional)
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Website"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  fullWidth
                  placeholder="https://yourwebsite.com"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Facebook Page"
                  name="facebook"
                  value={form.facebook}
                  onChange={handleChange}
                  fullWidth
                  placeholder="https://facebook.com/yourpage"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Instagram Handle"
                  name="instagram"
                  value={form.instagram}
                onChange={handleChange}
                fullWidth
                  placeholder="@yourbusiness"
              />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Submitting...' : 'Register Business'}
              </Button>
            </Box>
        </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProviderRegister; 